import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import FormField from './FormField';

interface PaymentModalProps {
  open: boolean;
  invoice: {
    id: number;
    doc_no: string;
    total: number;
    paid_amount: number;
  };
  onClose: () => void;
  onSaved: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ open, invoice, onClose, onSaved }) => {
  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount(0);
      setDate(new Date().toISOString().slice(0, 10));
      setNote('');
      setError(null);
    }
  }, [open]);

  const remaining = invoice.total - invoice.paid_amount;

  const handleFullPayment = () => {
    setAmount(remaining);
  };

  const handleSave = async () => {
    if (amount <= 0) {
      setError('金額は0より大きい値を入力してください');
      return;
    }
    if (!date) {
      setError('入金日を入力してください');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await window.api.invoice.recordPayment(invoice.id, amount, date, note);
      if (!res.ok) throw new Error(res.error || '保存に失敗しました');
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`入金記録 - ${invoice.doc_no}`}
      width="420px"
      footer={
        <>
          <button className="btn-default" onClick={onClose} disabled={saving}>
            キャンセル
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: '#6b7280' }}>請求額</span>
          <span style={{ fontWeight: 'bold' }}>{invoice.total.toLocaleString()} 円</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: '#6b7280' }}>既入金額</span>
          <span>{invoice.paid_amount.toLocaleString()} 円</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
          <span style={{ color: '#6b7280' }}>残高</span>
          <span style={{ fontWeight: 'bold', color: remaining > 0 ? '#ef4444' : '#10b981' }}>
            {remaining.toLocaleString()} 円
          </span>
        </div>
      </div>

      <FormField label="入金額" required error={error || undefined}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="number"
            value={amount || ''}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={{ flex: 1 }}
            min={0}
          />
          <button className="btn-default" onClick={handleFullPayment} style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
            全額入金
          </button>
        </div>
      </FormField>

      <FormField label="入金日" required>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ width: '100%' }}
        />
      </FormField>

      <FormField label="備考">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ width: '100%', minHeight: '60px', resize: 'vertical' }}
          placeholder="任意のメモ"
        />
      </FormField>
    </Modal>
  );
};

export default PaymentModal;
